import { useState, useRef, useEffect } from 'react';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/ui/Pagination';
import { createPortal } from 'react-dom';
import { PDFLink } from "../components/InvoicePDF";
import EmailModal from "../components/modals/EmailModal";

// Actions Dropdown Component
const ActionsDropdown = ({ quote, openModal, handleEmailQuote, convertQuoteToInvoice, deleteItem, settings, clients }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192 + window.scrollX // 192px = w-48 width
      });
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        type="button"
      >
        Actions
        <i className={`fas fa-chevron-down ml-2 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          {/* Dropdown Menu */}
          <div 
            className="absolute z-50 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left
            }}
          >
            <div className="py-1">
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  openModal('quote', quote);
                  setIsOpen(false);
                }}
              >
                <i className="fas fa-edit mr-3"></i>
                Edit
              </button>
              
              <PDFLink 
                item={quote} 
                type="quote" 
                clients={clients} 
                companySettings={settings}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setIsOpen(false)}
              />
              
              {/* Email Section */}
              <div className="border-t border-gray-100"></div>
              <button
                className={`flex items-center w-full px-4 py-2 text-sm ${
                  !settings?.smtpHost 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => {
                  if (settings?.smtpHost) {
                    handleEmailQuote(quote);
                    setIsOpen(false);
                  }
                }}
                disabled={!settings?.smtpHost}
              >
                <i className="fas fa-envelope mr-3"></i>
                <div className="flex flex-col items-start">
                  <span>Email Quote</span>
                  {!settings?.smtpHost && (
                    <small className="text-gray-400 text-xs">
                      (Email not configured)
                    </small>
                  )}
                </div>
              </button>
              
              {/* Convert to Invoice */}
              {quote.status === 'pending' && (
                <>
                  <div className="border-t border-gray-100"></div>
                  <button
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      convertQuoteToInvoice(quote);
                      setIsOpen(false);
                    }}
                  >
                    <i className="fas fa-exchange-alt mr-3"></i>
                    Convert to Invoice
                  </button>
                </>
              )}
              
              <div className="border-t border-gray-100"></div>
              <button
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  deleteItem(quote._id, 'quote');
                  setIsOpen(false);
                }}
              >
                <i className="fas fa-trash mr-3"></i>
                Delete
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

const QuotesPage = ({ 
  quotes, 
  clients, 
  openModal, 
  deleteItem, 
  convertQuoteToInvoice, 
  settings,
  onQuoteUpdate 
}) => {
  const [emailModal, setEmailModal] = useState({
    isOpen: false,
    quote: null,
    client: null
  });

  const {
    pageItems: pagedQuotes, page, setPage, pageSize, setPageSize,
    totalPages, total: totalQuotes, rangeStart, rangeEnd,
  } = usePagination(quotes, { initialPageSize: 25 });

  const formatDate = (date) => new Date(date).toLocaleDateString();
  const formatCurrency = (amount) => `${settings.currency}${parseFloat(amount || 0).toFixed(2)}`;
  
  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      converted: 'bg-blue-100 text-blue-800',
      sent: 'bg-indigo-100 text-indigo-800'
    };
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`;
  };

  const calculateTotal = (quote) => {
    const subtotal = quote.items?.reduce((sum, item) => sum + (item.quantity * item.price), 0) || 0;
    const discount = subtotal * (quote.discountPercentage || 0) / 100;
    const afterDiscount = subtotal - discount;
    const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
    return afterDiscount + tax;
  };

  const handleEmailQuote = (quote) => {
    const client = clients.find(c => c._id === quote.clientId);
    if (!client) {
      alert('Client not found for this quote');
      return;
    }
    
    setEmailModal({
      isOpen: true,
      quote: quote,
      client: client
    });
  };

  const handleEmailSent = (quote, recipientEmail) => {
    // Update quote status to 'sent' if it was in draft or pending
    if ((quote.status === 'draft' || quote.status === 'pending') && onQuoteUpdate) {
      onQuoteUpdate(quote._id, { ...quote, status: 'sent' });
    }
    
    console.log(`Quote ${quote.number} emailed to ${recipientEmail}`);
    // You could show a toast notification here
  };

  const closeEmailModal = () => {
    setEmailModal({
      isOpen: false,
      quote: null,
      client: null
    });
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h5 className="text-lg font-semibold text-gray-900 mb-0">Quotes</h5>
            <div className="ml-auto">
              <button
                onClick={() => openModal('quote')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <i className="fas fa-plus mr-2"></i>
                Create Quote
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-0">
          {quotes.length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-file-alt text-4xl text-gray-400 mb-4"></i>
              <h5 className="text-lg font-medium text-gray-500 mb-2">No quotes yet</h5>
              <p className="text-gray-500 mb-4">Create your first quote to get started</p>
              <button
                onClick={() => openModal('quote')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Create Quote
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedQuotes.map(quote => {
                    const client = clients.find(c => c._id === quote.clientId);
                    const total = calculateTotal(quote);
                    
                    return (
                      <tr key={quote._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => openModal('quote', quote)}
                            className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                          >
                            {quote.number}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {client?.name || (
                            <span className="text-gray-500">Unknown Client</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(quote.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {quote.validUntil ? (
                            <span className={new Date(quote.validUntil) < new Date() ? 'text-red-600' : 'text-gray-900'}>
                              {formatDate(quote.validUntil)}
                            </span>
                          ) : (
                            <span className="text-gray-500">No expiry</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={getStatusBadge(quote.status)}>
                            {quote.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {formatCurrency(total)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <ActionsDropdown 
                            quote={quote}
                            openModal={openModal}
                            handleEmailQuote={handleEmailQuote}
                            convertQuoteToInvoice={convertQuoteToInvoice}
                            deleteItem={deleteItem}
                            settings={settings}
                            clients={clients}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={totalQuotes}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          label="quotes"
        />

        {/* Summary Cards */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-sm text-gray-500">Total Quotes</div>
              <div className="text-lg font-semibold text-gray-900">{quotes.length}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Value</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatCurrency(quotes.reduce((sum, quote) => sum + calculateTotal(quote), 0))}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-lg font-semibold text-yellow-600">
                {quotes.filter(quote => quote.status === 'pending').length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      <EmailModal
        isOpen={emailModal.isOpen}
        onClose={closeEmailModal}
        document={emailModal.quote}
        documentType="quote"
        client={emailModal.client}
        companySettings={settings}
        onEmailSent={handleEmailSent}
      />
    </>
  );
};

export default QuotesPage;